import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { Col, Drawer, Form, Input, notification, Row } from 'antd';
import PropTypes from 'prop-types';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';
import { KeyValueWidget } from 'components/KeyValueWidget';

import { FORMS } from 'packages/externalCompanies/constants';
import { useRulesByCountry } from 'packages/utils/hooks/useRulesByCountry';
import { formatRut } from 'packages/utils/hooks/useRulesByCountry/constants';

import { FORM_LAYOUT_DEFAULT } from 'constants/commons';

import IntlMessages from 'util/IntlMessages';

const { Item } = Form;
function ExternalCompanyForm({
  visible,
  onClose,
  initialValues,
  onSubmit,
  loading,
  externalCompanies,
}) {
  const [externalCompanyForm] = Form.useForm();

  const intl = useIntl();

  const { countryRulesForm: { identifier } = {} } = useRulesByCountry();

  const validateName = async (name, currentId) => {
    // Check if name exists in your companies list
    const exists = externalCompanies.some(
      company => company.name.toLowerCase() === name.toLowerCase() && company.id !== currentId,
    );

    if (exists) {
      notification.error({
        message: 'Company name already exists',
        placement: 'topRight',
      });
      return true; // Name exists
    }
    return false; // Name is unique
  };

  const onFinish = async values => {
    // Validate name if validateName function is provided
    const nameExists = await validateName(values.name, initialValues?.id);
    if (nameExists) {
      return;
    }

    // Pass values to parent component
    onSubmit(values);
  };

  useEffect(() => {
    if (initialValues) {
      externalCompanyForm.setFieldsValue(initialValues);
    } else {
      externalCompanyForm.resetFields();
    }
  }, [externalCompanyForm, initialValues]);

  return (
    <Drawer closable={false} footer={null} open={visible}>
      <BoxContainer>
        <BoxContainer content shadow fixed>
          <FilterContainer
            title={
              <Title.Header
                value={
                  <IntlMessages
                    id={
                      !initialValues?.id
                        ? 'externalCompany.form.create'
                        : 'externalCompany.form.edit'
                    }
                  />
                }
              />
            }
            buttonItems={[
              {
                iconName: 'save',
                action: () => externalCompanyForm.submit(),
                disabled: loading,
              },
            ]}
            goBack={onClose}
          />
        </BoxContainer>
        <BoxContainer content loading={loading}>
          <Form
            form={externalCompanyForm}
            layout={FORM_LAYOUT_DEFAULT}
            name={FORMS.EXTERNAL_COMPANY}
            onFinish={onFinish}
          >
            <Item name="id" hidden initialValue={initialValues?.id} />
            <Row gutter={[8, 8]}>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm required value={<IntlMessages id="general.form.name" />} />
                  }
                  value={
                    <Item
                      rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.required.msg" />,
                        },
                      ]}
                      name="name"
                    >
                      <Input />
                    </Item>
                  }
                />
              </Col>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={<IntlMessages id="externalCompany.form.companyName" />}
                    />
                  }
                  value={
                    <Item
                      rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.required.msg" />,
                        },
                      ]}
                      name="tradeName"
                    >
                      <Input />
                    </Item>
                  }
                />
              </Col>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm required value={<IntlMessages id="general.form.email" />} />
                  }
                  value={
                    <Item
                      rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.required.msg" />,
                        },
                        {
                          type: 'email',
                          message: <IntlMessages id="general.email.error" />,
                        },
                      ]}
                      name="email"
                    >
                      <Input />
                    </Item>
                  }
                />
              </Col>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={<IntlMessages id="config.organization.rut" />}
                    />
                  }
                  value={
                    <Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.identifier !== currentValues.identifier
                      }
                    >
                      {({ setFieldValue }) => (
                        <Item
                          name="identifier"
                          rules={[
                            {
                              required: true,
                              message: <IntlMessages id="form.required.msg" />,
                            },
                            {
                              validator: async (_, value) => {
                                const valueWithoutDots = value.replace(/\./g, '');
                                if (valueWithoutDots.length < 9 || valueWithoutDots.length > 10) {
                                  return Promise.reject(
                                    new Error(
                                      intl.formatMessage({ id: 'country.identifier.invalid' }),
                                    ),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            onKeyUp={e =>
                              setFieldValue('identifier', formatRut(e.currentTarget.value))
                            }
                            placeholder={identifier?.pattern?.placeholder || ''}
                          />
                        </Item>
                      )}
                    </Item>
                  }
                />
              </Col>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <KeyValueWidget
                  label={<Title.LabelForm value={<IntlMessages id="general.form.phone" />} />}
                  value={
                    <Item
                      rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.required" />,
                        },
                        {
                          pattern: /^[0-9]*$/,
                          message: <IntlMessages id="form.must.be.number" />,
                        },
                      ]}
                      name="phone"
                    >
                      <Input />
                    </Item>
                  }
                />
              </Col>
            </Row>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
}

ExternalCompanyForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  externalCompanies: PropTypes.array.isRequired,
};

ExternalCompanyForm.defaultProps = {
  loading: false,
  initialValues: null,
};

export default ExternalCompanyForm;
